import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { containerVariants, itemVariants } from '../lib/animations';
import hopefulCloudAsset from '../../assets/icons/hopeful-cloud.svg';

const FRAME_W = 430;
const FRAME_H = 932;

const confettiParticles = [
  { left: 79, top: 301, w: 3, h: 8, rot: 51.5, color: '#DBF7F8' },
  { left: 138, top: 299, w: 3, h: 8, rot: -41.3, color: '#DBF7F8' },
  { left: 55, top: 246, w: 3, h: 8, rot: 28.7, color: '#5CA02B' },
  { left: 33, top: 491, w: 3, h: 8, rot: -32.9, color: '#5CA02B' },
  { left: 394, top: 436, w: 4.4, h: 5.6, rot: 28.7, color: '#5CA02B' },
  { left: 364, top: 414, w: 5.4, h: 8, rot: -41.3, color: '#DBF7F8' },
  { left: 202, top: 261, w: 3, h: 8, rot: 9.7, color: '#FF9EB8' },
  { left: 277, top: 286, w: 3, h: 8, rot: -41.3, color: '#DBF7F8' },
  { left: 296, top: 492, w: 3, h: 8, rot: -41.3, color: '#DBF7F8' },
  { left: 369, top: 344, w: 3, h: 8, rot: -39.7, color: '#DBF7F8' },
  { left: 144, top: 508, w: 5.5, h: 8, rot: -39.7, color: '#DBF7F8' },
  { left: 269, top: 243, w: 7, h: 8, rot: 10.1, color: '#DBF7F8' },
  { left: 357, top: 506, w: 3, h: 8, rot: 10.1, color: '#DBF7F8' },
  { left: 55, top: 458, w: 3, h: 8, rot: 10.1, color: '#DBF7F8' },
  { left: 171, top: 233, w: 4.5, h: 8, rot: 106.7, color: '#DBF7F8' },
  { left: 317, top: 334, w: 5.7, h: 8, rot: -176.8, color: '#FF9EB8' },
  { left: 103, top: 246, w: 6.9, h: 8, rot: -176.8, color: '#6F6FD4' },
  { left: 106, top: 510, w: 3, h: 8, rot: -102, color: '#6F6FD4' },
  { left: 68, top: 340, w: 6, h: 8, rot: -102, color: '#FFA100' },
  { left: 359, top: 238, w: 4.1, h: 5.3, rot: -102, color: '#FFA100' },
  { left: 42, top: 298, w: 4.3, h: 6, rot: -102, color: '#D32640' },
  { left: 317, top: 219, w: 3, h: 5.3, rot: -18.7, color: '#D32640' },
  { left: 331, top: 472, w: 4.5, h: 5.7, rot: -102, color: '#FFA100' },
  { left: 229, top: 291, w: 3, h: 5.3, rot: 61.8, color: '#FFA100' },
  { left: 137, top: 229, w: 3, h: 5.3, rot: -57.8, color: '#FFA100' },
  { left: 185, top: 525, w: 3, h: 5.3, rot: -1.1, color: '#FFA100' },
  { left: 246, top: 217, w: 3, h: 8, rot: -102, color: '#6F6FD4' },
  { left: 366, top: 284, w: 4.2, h: 8, rot: -102.2, color: '#6F6FD4' },
  { left: 368, top: 470, w: 3, h: 8, rot: -61.6, color: '#6F6FD4' },
  { left: 254, top: 513, w: 5.3, h: 8, rot: 121.1, color: '#FF9EB8' },
  { left: 43, top: 384, w: 3, h: 8, rot: -103.7, color: '#FF9EB8' },
  { left: 76, top: 504, w: 5.6, h: 8, rot: 106.7, color: '#DBF7F8' },
  { left: 335, top: 259, w: 3, h: 8, rot: 40.2, color: '#DBF7F8' },
  { left: 311, top: 524, w: 6.3, h: 8.1, rot: 40.2, color: '#D32640' },
  { left: 43, top: 419, w: 6.6, h: 8, rot: 140.8, color: '#D32640' },
  { left: 202, top: 492, w: 3, h: 8, rot: 40.2, color: '#DBF7F8' },
];

export default function AssessmentCompletedPage() {
  const { childId } = useParams<{ childId: string }>();
  const navigate = useNavigate();

  const profilePath = `/profile/${childId}`;

  useEffect(() => {
    const timer = setTimeout(() => {
      navigate(profilePath, { replace: true });
    }, 2000);
    return () => clearTimeout(timer);
  }, [navigate, profilePath]);

  return (
    <div
      className="min-h-screen flex flex-col relative overflow-hidden"
      style={{ backgroundColor: '#58C5DA' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-6 pt-[70px] w-full max-w-[430px] mx-auto">
        <span
          className="text-[#007CAD] font-black text-[20px] whitespace-nowrap"
          style={{ fontFamily: 'DM Sans', letterSpacing: '-1.2px', fontVariationSettings: "'opsz' 14" }}
        >
          Play Moments
        </span>
        <button
          type="button"
          onClick={() => navigate(profilePath, { replace: true })}
          className="w-12 h-12 flex items-center justify-center"
          aria-label="Close"
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M1 1L11 11M11 1L1 11" stroke="#1B2831" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      {/* Confetti layer */}
      <div
        className="absolute inset-0 w-full max-w-[430px] mx-auto pointer-events-none"
        style={{ transform: 'translateY(60px)' }}
      >
        {confettiParticles.map((p, i) => (
          <div
            key={i}
            className="absolute"
            style={{
              left: `${(p.left / FRAME_W) * 100}%`,
              top: `${(p.top / FRAME_H) * 100}%`,
              width: `${(p.w / FRAME_W) * 100}%`,
              height: `${(p.h / FRAME_W) * 50}%`,
              backgroundColor: p.color,
              transform: `rotate(${p.rot}deg)`,
            }}
          />
        ))}
      </div>

      {/* Animated content */}
      <motion.div
        className="flex-1 flex flex-col items-center justify-center px-6 pb-32 relative z-10"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.img
          src={hopefulCloudAsset}
          alt="Hopeful cloud"
          className="w-[208px] min-[414px]:w-[260px] max-w-full"
          variants={itemVariants}
        />

        <motion.h2
          className="absolute text-[#1B2831] font-bold text-[32px] md:text-[34px] lg:text-[36px] text-center max-w-[381px] top-[65%]"
          style={{ fontFamily: 'DM Sans', letterSpacing: '-1.28px', fontVariationSettings: "'opsz' 14" }}
          variants={itemVariants}
        >
          Creating your personalised sparks ...
        </motion.h2>
      </motion.div>
    </div>
  );
}
